/**
 * Valuation Service
 * Frontend service for Forward MtM valuation API calls
 */

import { apiClient, APIResponse } from './client';
import {
  CurveData,
  CurveSummary,
  CurveUploadResult,
  CurvePreviewResult,
  CurveConfirmRequest,
  TradeWithValuation,
  TradeValuationDetail,
  HistoricalTradeValuation,
  ValuationRunResult,
  ValuationUpload,
  ValuationBatchResult,
  FwdPointCurveData,
  FwdPointCurveSummary,
  FwdPointCurvePreviewResult,
  FwdPointCurveConfirmRequest,
  FwdPointCurveConfirmResult,
} from '../../types/valuation';

class ValuationService {
  // ============================================
  // Curve Management
  // ============================================

  /**
   * Upload curve file (Excel or CSV)
   * Requires manage_valuation permission
   */
  async uploadCurves(
    clientId: string,
    file: File,
    valuationDate: string,
    defaultDcc: string = 'ACT/360'
  ): Promise<APIResponse<CurveUploadResult>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('valuation_date', valuationDate);
    formData.append('default_day_count_convention', defaultDcc);

    return apiClient.postFormData<CurveUploadResult>(
      `/api/v1/valuation/${clientId}/curves/upload`,
      formData
    );
  }

  /**
   * Preview a curve file — parse without storing.
   * Returns detected curves with suggested DCCs per currency.
   */
  async previewCurves(
    clientId: string,
    file: File
  ): Promise<APIResponse<CurvePreviewResult>> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.postFormData<CurvePreviewResult>(
      `/api/v1/valuation/${clientId}/curves/preview`,
      formData
    );
  }

  /**
   * Confirm and store curves after preview, with user-chosen DCCs.
   */
  async confirmCurves(
    clientId: string,
    request: CurveConfirmRequest
  ): Promise<APIResponse<CurveUploadResult>> {
    return apiClient.post<CurveUploadResult>(
      `/api/v1/valuation/${clientId}/curves/confirm`,
      request
    );
  }

  /**
   * List available curves for a client, optionally filtered by date
   */
  async getCurves(
    clientId: string,
    valuationDate?: string
  ): Promise<APIResponse<CurveSummary[]>> {
    const queryParams = new URLSearchParams();
    if (valuationDate) queryParams.append('valuation_date', valuationDate);

    const queryString = queryParams.toString();
    const url = `/api/v1/valuation/${clientId}/curves${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<CurveSummary[]>(url);
  }

  /**
   * Get a specific curve's full data including tenor points
   */
  async getCurve(
    clientId: string,
    currency: string,
    date: string
  ): Promise<APIResponse<CurveData>> {
    return apiClient.get<CurveData>(
      `/api/v1/valuation/${clientId}/curves/${currency}/${date}`
    );
  }

  /**
   * Delete a specific curve
   * Requires manage_valuation permission
   */
  async deleteCurve(
    clientId: string,
    currency: string,
    date: string
  ): Promise<APIResponse<{ currency: string; valuationDate: string; deleted: boolean }>> {
    return apiClient.delete<{ currency: string; valuationDate: string; deleted: boolean }>(
      `/api/v1/valuation/${clientId}/curves/${currency}/${date}`
    );
  }

  // ============================================
  // Valuation Calculation
  // ============================================

  /**
   * Trigger MtM valuation run for all eligible Forward trades
   * Requires manage_valuation permission
   */
  async calculateValuations(
    clientId: string,
    valuationDate: string,
    forceRecalculate: boolean = false,
    forwardRateSource: 'cirp' | 'fwd_points' = 'cirp',
    discountingMethod: 'two_leg' | 'net_pnl' | 'auto' = 'auto',
    fwdPointsPrice: 'market' | 'mid' | 'bid' | 'ask' = 'market',
    valuationCurrency: string = 'CLP',
    asiaticoMethod: 'blended' | 'single_rate' = 'blended',
    asOfDate?: string,
    marketDataDate?: string,
    forwardStartingEligibility: 'confirmed_only' | 'include_provisional' = 'confirmed_only',
    forwardStartingMethod: 'single_rate' | 'blended' = 'single_rate',
    splitMtmLevel: 'parent' | 'split' = 'parent',
    ufSpotDateMode: string = 'inherit',
    spotDateMode: string = 'curve_date',
    valuationDateMode?: string,
    businessCenter?: string,
  ): Promise<APIResponse<ValuationRunResult>> {
    const body: Record<string, any> = { valuationDate, forceRecalculate, forwardRateSource, discountingMethod, fwdPointsPrice, valuationCurrency, asiaticoMethod, forwardStartingEligibility, forwardStartingMethod, splitMtmLevel, ufSpotDateMode, spotDateMode };
    if (asOfDate) body.asOfDate = asOfDate;
    if (marketDataDate) body.marketDataDate = marketDataDate;
    // executionDate: always send the raw valuationDate so the backend knows
    // which date the run was kicked off from (used when spotDateMode='execution_date')
    body.executionDate = valuationDate;
    // KAN-223: audit fields for valuation date mode
    if (valuationDateMode) body.valuationDateMode = valuationDateMode;
    if (businessCenter) body.businessCenter = businessCenter;
    return apiClient.post<ValuationRunResult>(
      `/api/v1/valuation/${clientId}/calculate`,
      body
    );
  }

  // ============================================
  // Forward Point Curve Management (KAN-118)
  // ============================================

  /**
   * Preview a forward point curve file — parse without storing.
   */
  async previewFwdPointCurves(
    clientId: string,
    file: File
  ): Promise<APIResponse<FwdPointCurvePreviewResult>> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.postFormData<FwdPointCurvePreviewResult>(
      `/api/v1/valuation/${clientId}/fwd-point-curves/preview`,
      formData
    );
  }

  /**
   * Confirm and store forward point curves after preview.
   */
  async confirmFwdPointCurves(
    clientId: string,
    request: FwdPointCurveConfirmRequest
  ): Promise<APIResponse<FwdPointCurveConfirmResult>> {
    return apiClient.post<FwdPointCurveConfirmResult>(
      `/api/v1/valuation/${clientId}/fwd-point-curves/confirm`,
      request
    );
  }

  /**
   * List available forward point curves, optionally filtered by date.
   */
  async getFwdPointCurves(
    clientId: string,
    valuationDate?: string
  ): Promise<APIResponse<FwdPointCurveSummary[]>> {
    const queryParams = new URLSearchParams();
    if (valuationDate) queryParams.append('valuation_date', valuationDate);

    const queryString = queryParams.toString();
    const url = `/api/v1/valuation/${clientId}/fwd-point-curves${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<FwdPointCurveSummary[]>(url);
  }

  /**
   * Get a specific forward point curve's full data including tenor points.
   */
  async getFwdPointCurve(
    clientId: string,
    pairKey: string,
    date: string
  ): Promise<APIResponse<FwdPointCurveData>> {
    return apiClient.get<FwdPointCurveData>(
      `/api/v1/valuation/${clientId}/fwd-point-curves/${pairKey}/${date}`
    );
  }

  /**
   * Delete a specific forward point curve.
   * Requires manage_valuation permission.
   */
  async deleteFwdPointCurve(
    clientId: string,
    pairKey: string,
    date: string
  ): Promise<APIResponse<{ pairKey: string; valuationDate: string; deleted: boolean }>> {
    return apiClient.delete<{ pairKey: string; valuationDate: string; deleted: boolean }>(
      `/api/v1/valuation/${clientId}/fwd-point-curves/${pairKey}/${date}`
    );
  }

  /**
   * Check whether spot rates exist in reference data for the given pairs and date.
   * Returns a map of pair → available (true/false).
   */
  async checkSpotRates(
    clientId: string,
    date: string,
    pairs: string[]
  ): Promise<APIResponse<Record<string, boolean>>> {
    const queryParams = new URLSearchParams({ date, pairs: pairs.join(',') });
    return apiClient.get<Record<string, boolean>>(
      `/api/v1/valuation/${clientId}/spot-rates/check?${queryParams}`
    );
  }

  /**
   * Fetch the three standard Chilean reference rates (USD Observado, EUR Observado, UF)
   * for a given date. Value is null if not yet published.
   */
  async getReferenceRates(
    clientId: string,
    date: string
  ): Promise<APIResponse<Array<{ key: string; label: string; description: string; value: number | null }>>> {
    return apiClient.get(
      `/api/v1/valuation/${clientId}/reference-rates?date=${date}`
    );
  }

  /**
   * Get latest valuations (trades with their current/previous valuation snapshots)
   */
  async getLatestValuations(
    clientId: string
  ): Promise<APIResponse<TradeWithValuation[]>> {
    return apiClient.get<TradeWithValuation[]>(
      `/api/v1/valuation/${clientId}/latest`
    );
  }

  // ============================================
  // Upload History
  // ============================================

  /**
   * Get upload history (both manual and calculated)
   */
  async getValuationUploads(
    clientId: string,
    limit?: number
  ): Promise<APIResponse<ValuationUpload[]>> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', String(limit));

    const queryString = queryParams.toString();
    const url = `/api/v1/valuation/${clientId}/uploads${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<ValuationUpload[]>(url);
  }

  // ============================================
  // Valuation Batch Detail
  // ============================================

  /**
   * Get all trade valuations for a specific historical batch (by uploadId)
   * Used for viewing batches older than current/previous (which are on trade docs)
   */
  async getValuationBatch(
    clientId: string,
    uploadId: string
  ): Promise<APIResponse<ValuationBatchResult>> {
    return apiClient.get<ValuationBatchResult>(
      `/api/v1/valuation/${clientId}/batch/${uploadId}`
    );
  }

  // ============================================
  // Valuation History
  // ============================================

  /**
   * Get all trade valuations for a specific historical date
   * (from the valuationHistory subcollection)
   */
  async getValuationsForDate(
    clientId: string,
    date: string
  ): Promise<APIResponse<HistoricalTradeValuation[]>> {
    return apiClient.get<HistoricalTradeValuation[]>(
      `/api/v1/valuation/${clientId}/history/${date}`
    );
  }

  /**
   * Get all historical valuations for a specific trade
   */
  async getTradeHistory(
    clientId: string,
    tradeId: string
  ): Promise<APIResponse<TradeValuationDetail[]>> {
    return apiClient.get<TradeValuationDetail[]>(
      `/api/v1/valuation/${clientId}/trades/${tradeId}/history`
    );
  }

  /**
   * Delete ALL valuation data (demo mode only)
   * Clears trade valuations, history subcollections, and upload records
   */
  async deleteAllValuations(
    clientId: string
  ): Promise<APIResponse<{ tradesCleared: number; historyEntriesDeleted: number; uploadsDeleted: number }>> {
    return apiClient.delete<{ tradesCleared: number; historyEntriesDeleted: number; uploadsDeleted: number }>(
      `/api/v1/valuation/${clientId}/all`
    );
  }

  // ============================================
  // Date Resolution (KAN-223)
  // ============================================

  /**
   * Resolve the as-of date for a valuation run using the client's business calendar.
   * Returns the resolved as-of date, business center, and isMonthEnd flag.
   */
  async resolveValuationDate(
    clientId: string,
    executionDate: string,
    valuationDateMode: string
  ): Promise<APIResponse<{
    executionDate: string;
    asOfDate: string;
    valuationDateMode: string;
    businessCenter: string;
    isMonthEnd: boolean;
  }>> {
    const params = new URLSearchParams({ executionDate, valuationDateMode });
    return apiClient.get(`/api/v1/valuation/${clientId}/resolve-date?${params.toString()}`);
  }
}

export const valuationService = new ValuationService();
